import React, { Component } from "react";
import { Button } from "@material-ui/core";
import Form, {FormStatus} from "components/forms/Form";
import TextField from "components/forms/TextField";
import {RouteComponentProps} from 'react-router';
import {ValidationGroup, Validators} from 'util/validation';
import {resetPassword} from 'services/auth';
import RouteURLs from 'RouteURLs';
import AuthLayout from 'layouts/Authentication';
import ProgressSpinner from 'components/ProgressSpinner';
import {CheckIcon, InvalidIcon} from 'components/Icon';
import {logPageView} from 'services/analytics';
import './index.scss';
import {ValueError} from 'util/errors';

interface ResetPasswordPageState {
  status: FormStatus;
  error?: string;
  passwordErrors: string[]
}

interface ResetPasswordRequest {
  password: string;
  confirmedPassword: string;
}

interface ResetPasswordPageProps extends  RouteComponentProps<{token:string}> {
}

export default class ResetPasswordPage extends Component<ResetPasswordPageProps, ResetPasswordPageState> {
  private readonly validation: ValidationGroup;
  private readonly model: ResetPasswordRequest;

  state = {
    status: FormStatus.None,
    error: undefined,
    passwordErrors: []
  };

  constructor(props: any) {
    super(props);
    this.model = { password: "", confirmedPassword: "" };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("password", {
      required: Validators.required,
      minLength: Validators.minLength(8),
      minNumbers: Validators.containsNumbers(1),
      minUpperCase: Validators.containsUpperCase(1),
      minLowerCase: Validators.containsLowerCase(1)
    });
    this.validation.addField("confirmedPassword", {
      required: Validators.required,
      match: (val) => val === this.model.password
    });
  }

  componentDidMount(): void {
    logPageView(RouteURLs.RESET_PASSWORD, 'Reset Password');
    if(!this.props.match.params.token) {
      this.props.history.replace(RouteURLs.NOT_FOUND);
    } else {
      this.validation.validateField('password');
      this.setState({'passwordErrors': this.validation.getFieldErrors('password')});
      this.validation.addFieldListener('password', this.onPasswordValidationChanged);
    }
  }

  componentWillUnmount(): void {
    this.validation.removeFieldListener('password', this.onPasswordValidationChanged);
  }

  onPasswordValidationChanged = (errors: string[]) => {
    this.setState({'passwordErrors': errors});
  };

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    resetPassword(this.model.password, this.props.match.params.token).then(
      () => {
        this.setState({ status: FormStatus.Success });
      },
      err => {
        if (err instanceof ValueError) {
          let generalError = err.errors.length > 0 ? err.errors[0] : undefined;
          for(const key of Object.keys(err.fieldErrors)) {
            if(key === 'token') {
              const fieldErrors = err.fieldErrors[key];
              if(fieldErrors.length > 0) {
                generalError = fieldErrors[0];
              }
            } else {
              for (const fieldError of err.fieldErrors[key]) {
                this.validation.addFieldError(key, fieldError);
              }
            }
          }
          this.setState({
            status: FormStatus.Failure,
            error: generalError
          });
        } else {
          this.setState({
            status: FormStatus.Failure,
            error: "Unknown error."
          });
        }
      }
    );
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  onLoginClick = () => {
    this.props.history.push(RouteURLs.LOGIN);
  };

  renderPasswordRequirements() {
    const passwordErrors: string[] = this.state.passwordErrors;
    const requirements = [
      {error: 'minLength', label: 'Eight Characters'},
      {error: 'minUpperCase', label: 'One capital letter'},
      {error: 'minLowerCase', label: 'One lowercase letter'},
      {error: 'minNumbers', label: 'One number'}
    ];
    return <div className="password-requirements">
      <p>Your password must contain at least:</p>
      <ul>
        { requirements.map((requirement) => {
          const isValid = passwordErrors.indexOf(requirement.error) < 0;
          return <li className={isValid ? 'valid' : 'invalid'}>
            {isValid && <CheckIcon /> }
            {!isValid && <InvalidIcon /> }
            { requirement.label }</li>;
        }) }
      </ul>
    </div>;
  }
    
  render() {
    const disabled = this.state.status === FormStatus.Submitting || this.state.status === FormStatus.Success;
    return <AuthLayout id="reset-password-page">
      { this.state.status !== FormStatus.Success &&
      <Form onSubmit={this.onSubmit} validationGroup={this.validation}>
        <h1>Set your new password</h1>
        <p>Please enter your new password</p>
        <TextField
          name="password"
          label="Password"
          type="password"
          required={true}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{
            required: "Password is required",
            minLength: 'Password must be at least 8 characters',
            minNumbers: 'Password must contain at least 1 number',
            minUpperCase: 'Password must contain at least 1 upper case letter',
            minLowerCase: 'Password must contain at least 1 lower case letter'
          }}
          disabled={disabled}
          onInput={this.onFieldInput}/>
        <TextField
          name="confirmedPassword"
          label="Confirm password"
          type="password"
          required={true}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{
            required: "Password confirmation is required",
            match: 'Password confirmation does not match'
          }}
          disabled={disabled}
          onInput={this.onFieldInput}/>
        <div className="actions">
          <Button className="submit" type="submit">
            {this.state.status === FormStatus.Submitting && (
              <ProgressSpinner />
            )}
            Set password</Button>
        </div>
        {this.state.status === FormStatus.Failure && (
          <p className="error">{this.state.error}</p>
        )}
        { this.renderPasswordRequirements() }
      </Form>}
      { this.state.status === FormStatus.Success &&
      <section className="success">
        <h1>Password updated</h1>
        <p>Your new password has been set, you’re back up and running!  </p>
        <p>Click below to log back in to Sook.</p>
        <Button onClick={this.onLoginClick}>Back to log in</Button>
      </section>}
    </AuthLayout>;
  }
}
